/* Gentle nudges — preferences and local scheduling.
 *
 * Philosophy:
 *   - Default OFF. Always.
 *   - Nudges are emotionally-tuned phrases the user can edit.
 *   - Frequency-capped (max 3/week by default; cannot exceed daily).
 *   - Never urgency-based. Never retention-optimized.
 *   - All data lives on this device — no server, no analytics.
 *   - When the OS won't deliver (web context, tab closed), we show a
 *     quiet in-app card on the user's next visit. No alarms, no badges.
 *
 * This module is purposely framework-agnostic and side-effect-free except
 * for the localStorage read/write inside `loadPrefs`/`savePrefs`.
 */

const PREFS_KEY = "un_nudges_prefs_v1";

export const DEFAULT_NUDGES = [
  "A message is waiting for tomorrow-you.",
  "Protect your peace first.",
  "You do not have to decide tonight.",
  "Reach before the spiral.",
  "One safer next step is enough.",
];

export const FREQUENCY_OPTIONS = [
  { id: "weekly", label: "once a week", perWeek: 1 },
  { id: "three", label: "up to 3 a week", perWeek: 3 },
  { id: "daily", label: "once a day", perWeek: 7 },
];

const DEFAULTS = {
  enabled: false,
  frequencyId: "three",
  library: DEFAULT_NUDGES.slice(),
  lastShownAt: null, // ISO
  recentTimestamps: [], // last 14 days of shown nudge ISO timestamps
};

export function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULTS,
      ...parsed,
      library:
        Array.isArray(parsed.library) && parsed.library.length > 0
          ? parsed.library
          : DEFAULT_NUDGES.slice(),
      recentTimestamps: Array.isArray(parsed.recentTimestamps)
        ? parsed.recentTimestamps
        : [],
    };
  } catch (e) {
    console.warn("[nudges] loadPrefs failed, returning defaults:", e?.message || e);
    return { ...DEFAULTS };
  }
}

export function savePrefs(patch) {
  const current = loadPrefs();
  const next = { ...current, ...patch };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch (e) {
    // Storage quota or private-mode failure — same UX posture (silent to user),
    // but visible in the console so we can diagnose if it ever matters.
    console.warn("[nudges] savePrefs failed:", e?.message || e);
  }
  return next;
}

export function permissionStatus() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function requestPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch (e) {
    console.warn("[nudges] notification permission request failed:", e?.message || e);
    return "denied";
  }
}

function pruneRecent(timestamps, now = Date.now()) {
  const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
  return timestamps.filter((t) => new Date(t).getTime() > weekAgo);
}

export function isWithinFrequencyCap(prefs = loadPrefs(), now = Date.now()) {
  const opt = FREQUENCY_OPTIONS.find((o) => o.id === prefs.frequencyId) || FREQUENCY_OPTIONS[1];
  const recent = pruneRecent(prefs.recentTimestamps, now);
  return recent.length < opt.perWeek;
}

/** Was the last nudge shown today already? Avoid same-day repeats. */
export function shownToday(prefs = loadPrefs(), now = Date.now()) {
  if (!prefs.lastShownAt) return false;
  const last = new Date(prefs.lastShownAt);
  const d = new Date(now);
  return (
    last.getFullYear() === d.getFullYear() &&
    last.getMonth() === d.getMonth() &&
    last.getDate() === d.getDate()
  );
}

export function pickNudge(prefs = loadPrefs()) {
  const lib = prefs.library && prefs.library.length > 0 ? prefs.library : DEFAULT_NUDGES;
  return lib[Math.floor(Math.random() * lib.length)];
}

/** Record that a nudge was shown (system or in-app card). */
export function recordShown(prefs = loadPrefs(), now = Date.now()) {
  const recent = pruneRecent([...prefs.recentTimestamps, new Date(now).toISOString()], now);
  return savePrefs({
    lastShownAt: new Date(now).toISOString(),
    recentTimestamps: recent,
  });
}

/** Should a nudge surface on this visit? Honors: enabled, cap, daily de-dupe. */
export function shouldNudgeNow(prefs = loadPrefs(), now = Date.now()) {
  if (!prefs.enabled) return false;
  if (permissionStatus() === "unsupported") {
    // No system notification, but we can still show an in-app card.
  }
  if (shownToday(prefs, now)) return false;
  if (!isWithinFrequencyCap(prefs, now)) return false;
  return true;
}

/** Try to show via system; return true if shown via system, false otherwise. */
export async function showSystemNudge(text) {
  if (typeof Notification === "undefined") return false;
  if (Notification.permission !== "granted") return false;
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification("UnAnonymous", {
        body: text,
        icon: "/icon-192.png",
        badge: "/icon-192.png",
        silent: true, // never alarming
        tag: "un-nudge",
        renotify: false,
      });
      return true;
    }
    // Fallback to page-level notification
    // eslint-disable-next-line no-new
    new Notification("UnAnonymous", { body: text, silent: true });
    return true;
  } catch (e) {
    console.warn("[nudges] showSystemNudge failed:", e?.message || e);
    return false;
  }
}
